import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import AmpStoryPlayer from '../AmpStoryPlayer/ampStoryPlayer';
import { PlayerInstance } from '../AmpStoryPlayer/ampStoryPlayer.types';
import { StoryPlayerProps } from './storyPlayer.types';

import { UrlUtils } from './utils';

const noop = () => {};

const StoryPlayer: FC<StoryPlayerProps> = ({
  currentStoryUrl,
  disablePlaybackUrlOverride = false,
  exitControl,
  lang = 'en',
  onBack = noop,
  onClose = noop,
  onNavigation = noop,
  onNoPreviousStory = noop,
  onNoNextStory = noop,
  onMuteChange = noop,
  onPlayerReady = noop,
  onStoryNavigation = noop,
  onTouchStart = noop,
  onTouchMove = noop,
  onTouchEnd = noop,
  onAttachmentOpen = noop,
  onAttachmentClose = noop,
  playlist,
  referrer,
  playbackUrlConfig = {},
  stories,
  ...restProps
}) => {
  const [player, setPlayer] = useState<PlayerInstance | null>(null);
  const urlUtils = useMemo(() => new UrlUtils(
    playbackUrlConfig,
    { lang, playlist, referrer },
    disablePlaybackUrlOverride,
  ), [
    disablePlaybackUrlOverride,
    playbackUrlConfig,
    lang,
    playlist,
    referrer,
  ]);

  useEffect(() => {
    if (!player) {
      return;
    }

    const originalPlayerShow = player.show;
    player.show = (storyUrl: string) => {
      try {
        originalPlayerShow(urlUtils.getStoryWithPlaybackCanonical(storyUrl));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[StoryPlayer > player.show override]', e);
      }
    };
  }, [player, urlUtils]);

  const onPlayerReadyHandler = useCallback((playerInstance) => {
    onPlayerReady(playerInstance);
    setPlayer(playerInstance);
  }, [onPlayerReady, setPlayer]);

  useEffect(() => {
    if (!player) {
      return;
    }

    const onNavigationHandler = (event: CustomEvent) =>  {
      event.detail.href = urlUtils.getPlaybackUrl(stories[event.detail.index].href);
      return onNavigation(event);
    };

    // Triggers on Story mute/unmute button click
    player.addEventListener('amp-story-muted-state', onMuteChange);
    // Triggers on back button click
    player.addEventListener('amp-story-player-back', onBack);
    // Triggers on close button click
    player.addEventListener('amp-story-player-close', onClose);
    // Triggers on Story Switch (only when there is more than one story in the player)
    player.addEventListener('navigation', onNavigationHandler);
    // Triggers on navigation within a story (switching slides)
    player.addEventListener('storyNavigation', onStoryNavigation);
    // Triggers when there's no next story (disabled when circular wrapping is on)
    player.addEventListener('noNextStory', onNoNextStory);
    // Triggers when there's no previous story (disabled when circular wrapping is on)
    player.addEventListener('noPreviousStory', onNoPreviousStory);
    // These events only trigger on touch events, not pointer events
    player.addEventListener('amp-story-player-touchstart', onTouchStart);
    player.addEventListener('amp-story-player-touchmove', onTouchMove);
    player.addEventListener('amp-story-player-touchend', onTouchEnd);
    // Triggers when an attachment is opened/closed
    player.addEventListener('page-attachment-open', onAttachmentOpen);
    player.addEventListener('page-attachment-close', onAttachmentClose);

    return () => {
      player.removeEventListener('amp-story-muted-state', onMuteChange);
      player.removeEventListener('amp-story-player-back', onBack);
      player.removeEventListener('amp-story-player-close', onClose);
      player.removeEventListener('navigation', onNavigationHandler);
      player.removeEventListener('storyNavigation', onStoryNavigation);
      player.removeEventListener('noNextStory', onNoNextStory);
      player.removeEventListener('noPreviousStory', onNoPreviousStory);
      player.removeEventListener('amp-story-player-touchstart', onTouchStart);
      player.removeEventListener('amp-story-player-touchmove', onTouchMove);
      player.removeEventListener('amp-story-player-touchmove', onTouchEnd);
      player.removeEventListener('page-attachment-open', onAttachmentOpen);
      player.removeEventListener('page-attachment-close', onAttachmentClose);
    };
  }, [
    stories,
    player,
    onBack,
    onClose,
    onMuteChange,
    onNavigation,
    onNoNextStory,
    onNoPreviousStory,
    onStoryNavigation,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onAttachmentOpen,
    onAttachmentClose,
    urlUtils,
  ]);

  useEffect(() => {
    if (player && currentStoryUrl) {
      player.show(currentStoryUrl);
    }
  }, [player, currentStoryUrl]);

  useEffect(() => {
    if (!player || !stories.length) {
      return;
    }

    // We want to trigger navigation event for the first story
    // For history navigation purposes, we want the transformed URL
    if (!currentStoryUrl || stories[0].href === currentStoryUrl) {
      const navigation = new CustomEvent('navigation', {
        detail: {
          index: 0,
          remaining: stories.length - 1,
        }
      });
      player.dispatchEvent(navigation);
    }
  }, [player]); // Only `player` belongs here because we only want to run this once

  const updatedStories = useMemo(() => stories.map((story) => ({
    ...story,
    href: urlUtils.getStoryWithPlaybackCanonical(story.href),
  })),[stories, urlUtils]);

  return (
    <AmpStoryPlayer
      stories={updatedStories}
      onPlayerReady={onPlayerReadyHandler}
      {...( exitControl && { 'exit-control': `${exitControl}-button` } )}
      {...restProps}
    />
  );
};

export default StoryPlayer;
